/**
 * Ausência por inatividade (spec 072) — as decisões da tela, puras (provadas
 * no apps/api/src/shared/presence-idle.test.ts, porque o web não tem
 * executor de testes):
 *  - quando a aba avisa a atividade: só por gesto, no máximo um por minuto;
 *  - quando o aviso de volta aparece: Ausente por inatividade, e ainda não
 *    dispensado NESTE navegador para ESTA queda.
 */
import {
  PRESENCE_ACTIVITY_THROTTLE_MS,
  PRESENCE_IDLE_DEFAULT_MINUTES,
  type Presence,
} from '@erp/shared';

/** gesto da pessoa — nunca o que a tela faz sozinha (refetch, socket, timer) */
export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** a queda dispensada ("Continuar ausente"), compartilhada entre as abas */
export const IDLE_DISMISSED_KEY = 'erp.presence.idle-dismissed';

/** o primeiro gesto avisa na hora; os seguintes, só depois do intervalo */
export function shouldSendActivity(
  lastSentAt: number | null,
  now: number,
  throttleMs: number = PRESENCE_ACTIVITY_THROTTLE_MS
): boolean {
  return lastSentAt === null || now - lastSentAt >= throttleMs;
}

/** só quem tem status aberto avisa: Ausente não tem intervalo onde gravar */
export function tracksActivity(me: Pick<Presence, 'status'> | undefined): boolean {
  return me !== undefined && me.status !== 'away';
}

/**
 * O "desde" do aviso de volta (ISO), ou null quando ele não aparece. Comparado
 * em ISO: o servidor manda texto, e a queda dispensada foi gravada a partir
 * dele.
 */
export function idleDialogSince(
  me: Pick<Presence, 'status' | 'idleSince'> | undefined,
  dismissed: string | null
): string | null {
  if (!me || me.status !== 'away' || !me.idleSince) return null;
  const since = new Date(me.idleSince);
  if (Number.isNaN(since.getTime())) return null;
  const iso = since.toISOString();
  return iso === dismissed ? null : iso;
}

/** "14:10" no mesmo dia; "03/11 18:20" em outro */
export function idleSinceLabel(since: string | Date, now: number): string {
  const d = new Date(since);
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(now);
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return time;
  const day = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${day} ${time}`;
}

/** o limite como a tela o diz: "Desligado", "45 min", "1 h", "1 h 30" */
export function idleMinutesLabel(minutes: number): string {
  if (minutes === 0) return 'Desligado';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
}

/** as escolhas da página Pausas; o valor salvo entra na lista se não for uma delas */
export function idleMinutesOptions(current: number | undefined): number[] {
  const base = [0, 15, 30, 45, PRESENCE_IDLE_DEFAULT_MINUTES, 90, 120, 180, 240, 480];
  if (current === undefined || base.includes(current)) return base;
  return [...base, current].sort((a, b) => a - b);
}
